.hero {
  position: relative;
  background: var(--bg-dark);
  color: var(--text-on-dark);
}

/* Full-bleed mobile flyer band (Main.dc.html) - lives directly in .hero, not .inner, so it can
   span edge to edge while .inner still gets its own side padding for the text below. */
.mobileFlyerBand {
  position: relative;
  display: block;
  width: 100%;
  /* Tall enough for the frame (up to 260 * 4/3 ~ 347px once it hits its max-width, plus its own
     24px top margin) AND the caption below it, with room to spare - a tighter height clipped the
     bottom of the frame itself via this element's own overflow:hidden, before the badge-overlap
     bug (below) was even a factor. */
  height: 420px;
  overflow: hidden;
  background: var(--bg-dark);
  border: none;
  padding: 0;
  cursor: pointer;
}

.flyerCaption {
  display: block;
  text-align: center;
  margin-top: 10px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-on-dark-muted);
}

.mobileFlyerBackdrop {
  position: absolute;
  inset: -10%;
  width: 120%;
  height: 120%;
  object-fit: cover;
  filter: blur(20px) brightness(0.45);
  opacity: 0.9;
}

.mobileFlyerFrame {
  position: relative;
  margin: 24px auto 0;
  /* Was a fixed `width: 62%; aspect-ratio: 3/4` box with the img object-fit:contain'd inside it -
     fine for a near-3:4 flyer, but a horizontal one got shrunk to fit that box's WIDTH, leaving
     large empty letterboxed bars above/below (confirmed live against a real landscape flyer).
     fit-content instead sizes the frame to whatever box .mobileFlyerImg's own constraints below
     actually render at, so a landscape flyer gets a landscape (short) frame and a portrait one
     still gets a tall frame - no wasted space either way. */
  width: fit-content;
  border-radius: var(--r-12);
  overflow: hidden;
  background: var(--bg-dark);
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.55);
}

.mobileFlyerImg {
  display: block;
  width: auto;
  height: auto;
  /* Caps, not a forced box - the browser scales the image down to fit both at once while keeping
     its real aspect ratio (same "fit within both constraints" replaced-element sizing object-fit
     relies on, just without a parent box to object-fit against). 62vw mirrors the old 62%-of-band
     width cap (the mobile flyer band is full-bleed/viewport-width, so vw and % of it are
     equivalent here); 347px mirrors the old aspect-ratio box's own height ceiling
     (260px max-width * 4/3), so an extreme-portrait flyer still can't blow out the hero. */
  max-width: min(62vw, 260px);
  max-height: 347px;
}

.inner {
  max-width: var(--content-max);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
  /* padding-bottom is a deliberate safety margin: the registration card's overlap (App.module.css)
     is sized to stay within it when there's no flyer image, so the card can never collide with
     text content regardless of how many lines the title wraps to (padding is always blank space
     after the content, no matter how tall the content gets - unlike a fixed negative margin sized
     around "how tall the hero usually is with a flyer image", which breaks for a flyer-less event
     with a long title: confirmed live against a real event, no flyer + a 3-line title). */
  padding: 20px 20px 48px;
}

.flyerButton {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 160px;
  border: none;
  padding: 0;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
}

.flyerImg {
  /* height:auto (not a forced `aspect-ratio: 3/4`) so a landscape flyer gets a short, correctly-
     shaped thumbnail instead of being object-fit:contain'd into a tall box it doesn't fill - same
     "frame fits the image, not the other way round" fix as .mobileFlyerFrame/.mobileFlyerImg
     above. max-height is a safety cap for an extreme-portrait flyer (280px-wide * 4/3, the old
     box's own height) - object-fit:contain only matters in that rare clamped case now, not for
     every image. border-radius/box-shadow live directly on the img itself (rather than a separate
     clipping wrapper) now that the caption is a sibling below it, not absolutely overlaid on top
     of it (that overlap used to hide real flyer content near the bottom edge - confirmed live
     against a real event). */
  display: block;
  width: 100%;
  height: auto;
  /* Matches .flyerButton's own base width (160px) * the old 4/3 ratio ceiling - overridden below
     alongside .flyerButton's 280px width, since this component only ever actually renders at the
     >=1200px breakpoint (isMobile is false only there) where that override always applies. */
  max-height: 213px;
  object-fit: contain;
  border-radius: var(--r-12);
  background: var(--bg-dark);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.5);
}

.details {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dateLine {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--accent-tint-text);
}

.title {
  margin: 0 0 12px;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 32px;
  line-height: 1.05;
  text-transform: uppercase;
}

.trackLine {
  margin-top: -3px;
  font-size: 15px;
  color: var(--text-on-dark-muted);
}
.trackLine strong {
  color: var(--text-on-dark);
}

.actions {
  display: flex;
  gap: 10px;
  margin-top: 6px;
}

.actionBtn {
  height: 44px;
  padding: 0 16px;
  border-radius: var(--r-10);
  border: 1px solid #6b6f76;
  color: var(--text-on-dark);
  text-decoration: none;
  font-weight: 600;
  font-size: 15px;
  display: flex;
  align-items: center;
  background: transparent;
}

@media (min-width: 1200px) {
  .inner {
    flex-direction: row;
    gap: 32px;
    align-items: flex-start;
    /* Right padding reserves the EXACT same footprint the rail+its gap occupy in
       App.module.css's .desktopGrid (var(--rail-w) + 40px gap) - not a guess. .desktopGrid sits
       in .body, which shares this element's own max-width/margin:auto/60px-base-padding
       centering, so both containers' content areas start and end at identical x-coordinates at
       any viewport width. Previously each was sized independently (this row's own intrinsic
       width vs. .main's flex-shrink against the rail), so they only ever matched by coincidence
       at wide-enough viewports - confirmed broken live at ~1216px: the title bled into the
       rail's own space because .main had shrunk below this row's fixed width. Reserving the
       identical space here, then letting .details flex/shrink within it exactly like .main does
       below, makes them track each other at every width instead of coincidentally. */
    padding: 36px calc(60px + var(--rail-w) + 40px) 56px 60px;
  }
  .flyerButton {
    width: 280px;
  }
  .flyerImg {
    max-height: 373px;
  }
  .title {
    /* The extra 12px below the title is mobile-only (explicit feedback scoped it there) - back to
       flush on desktop, where .details' flex `gap` already spaces things out. */
    margin-bottom: 0;
    font-size: 48px;
  }
  .details {
    flex: 1;
    min-width: 0;
    max-width: 468px;
  }
}
